import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/site/BackButton";
import { faqPageJsonLd } from "@/lib/seo";

const SHIPPING_FAQ = [
  { question: "Where does KAKAIYS ship?", answer: "KAKAIYS ships worldwide from our atelier in Sialkot, Pakistan, to over 180 countries including the US, UK, EU, Canada, Australia, the GCC and most of Asia." },
  { question: "How long does processing take?", answer: "Every piece is hand-finished after your order is placed. Standard processing takes 6 to 9 business days from order confirmation to dispatch." },
  { question: "How much does shipping cost?", answer: "Shipping is complimentary on every order worldwide. There are no minimums and no hidden handling fees — every piece is delivered via fully tracked, insured express courier at no additional cost." },
  { question: "Who pays import duties and taxes?", answer: "All international orders ship DDU (Delivered Duty Unpaid). Any import duties, customs fees or local taxes levied by the destination country are the responsibility of the recipient." },
  { question: "How do I track my order?", answer: "Once dispatched you will receive a tracking number by email and can track your shipment in real time via the courier's portal. If you have not received tracking within 5 business days of placing your order, contact care@kakaiys.com." },
  { question: "What happens if my parcel is lost or damaged?", answer: "Every shipment is fully insured. In the rare event your parcel arrives damaged or is lost in transit, contact us within 7 days of the expected delivery date and we will arrange a replacement or full refund." },
];

export const Route = createFileRoute("/shipping")({
  head: () => ({
    meta: [
      { title: "Shipping Policy — KAKAIYS" },
      {
        name: "description",
        content:
          "Worldwide shipping from our atelier in Sialkot. Processing times, delivery estimates, duties and tracking for every KAKAIYS order.",
      },
      { property: "og:title", content: "Shipping Policy — KAKAIYS" },
      {
        property: "og:description",
        content: "Worldwide shipping from the KAKAIYS atelier — fully tracked, insured and made with care.",
      },
      { property: "og:url", content: "https://kakaiys.com/shipping" },
    ],
    links: [{ rel: "canonical", href: "https://kakaiys.com/shipping" }],
    scripts: [
      { type: "application/ld+json", children: faqPageJsonLd(SHIPPING_FAQ) },
    ],
  }),
  component: ShippingPage,
});

function ShippingPage() {
  return (
    <article className="pt-40 pb-24 container-luxe max-w-3xl">
      <BackButton fallback="/" className="mb-8" />
      <div className="eyebrow mb-5">Maison · Shipping</div>
      <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-12">Shipping Policy</h1>

      <div className="prose-luxe space-y-10 text-foreground/85">
        <section>
          <h2 className="font-serif text-2xl mb-3">Where we ship</h2>
          <p>
            KAKAIYS ships worldwide from our atelier in Sialkot, Pakistan. We deliver to over 180 countries
            including the United States, United Kingdom, European Union, Canada, Australia, the GCC and most
            of Asia. If your country does not appear at checkout, please contact our Customer Care team.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Processing time</h2>
          <p>
            Every piece is hand-finished after your order is placed. Standard processing takes
            <strong> 6–9 business days</strong> from order confirmation to dispatch. You will receive an
            email with tracking information the moment your piece leaves the atelier.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Delivery estimates</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Pakistan:</strong> 2–4 business days</li>
            <li><strong>United States &amp; Canada:</strong> 4–7 business days (DHL / FedEx Express)</li>
            <li><strong>United Kingdom &amp; Europe:</strong> 3–6 business days (DHL Express)</li>
            <li><strong>Middle East &amp; GCC:</strong> 2–4 business days</li>
            <li><strong>Australia, New Zealand, Asia:</strong> 5–8 business days</li>
            <li><strong>Rest of world:</strong> 7–14 business days</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Shipping cost</h2>
          <p>
            Shipping is <strong>complimentary on every order, worldwide</strong>. There are no
            minimums and no hidden handling fees — every KAKAIYS piece is delivered to your door
            via fully tracked, insured express courier at no additional cost.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Duties &amp; taxes</h2>
          <p>
            All international orders are shipped <strong>DDU (Delivered Duty Unpaid)</strong>. Any import
            duties, customs fees or local taxes levied by the destination country are the responsibility of
            the recipient. We declare every parcel at full retail value as required by international law.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Tracking your order</h2>
          <p>
            Once dispatched, you will receive a tracking number by email. You can track your shipment in
            real time via the courier's portal. If you have not received tracking within 5 business days of
            placing your order, please contact us at <a className="text-gold underline-grow" href="mailto:care@kakaiys.com">care@kakaiys.com</a>.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Lost or damaged parcels</h2>
          <p>
            Every shipment is fully insured. In the rare event your parcel arrives damaged or is lost in
            transit, contact us within 7 days of the expected delivery date and we will arrange a
            replacement or full refund.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Failed delivery &amp; refused parcels</h2>
          <p>
            If a parcel is returned to us because the recipient was unreachable, the address was incorrect,
            customs duties were refused, or delivery was declined, we will contact you to arrange
            redelivery. Redelivery shipping costs and any return-to-origin charges levied by the courier
            are the responsibility of the customer.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Force majeure</h2>
          <p>
            Delivery times are estimates and may be affected by events beyond our reasonable control —
            including customs inspections, courier disruption, strikes, severe weather, natural disasters,
            pandemics or government action. We are not liable for delays caused by such events but will
            keep you informed and work to deliver your order as soon as possible.
          </p>
        </section>
      </div>
    </article>
  );
}
